/**
 * HEIC/HEIF → JPEG conversion, shared by the bulk photo sorter, mobile capture,
 * and desktop uploader. iPhones default to HEIC and no browser except Safari
 * decodes it natively — without this, previews render as broken images and
 * canvas-based compression fails silently.
 *
 * Key considerations:
 * - Lazy-loaded (~600KB WASM) so the main bundle stays lean.
 * - Preserves the original File name with a .jpg extension.
 * - Falls back to the original file on ANY failure so the upload never breaks.
 * - EXIF metadata is STRIPPED by heic2any — callers that need DateTimeOriginal
 *   (e.g. bulk photo sorter for time-clustering) MUST parse EXIF from the raw
 *   HEIC blob BEFORE calling this converter. exifr handles HEIC natively.
 */

// Cache the imported module across calls so we only pay the network cost once.
let heic2anyPromise = null;
const loadHeic2any = () => {
  if (!heic2anyPromise) heic2anyPromise = import("heic2any").then((m) => m.default);
  return heic2anyPromise;
};

export function isHeic(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

export async function convertHeicToJpeg(file, { quality = 0.9 } = {}) {
  if (!isHeic(file)) return file;
  try {
    const heic2any = await loadHeic2any();
    const converted = await heic2any({ blob: file, toType: "image/jpeg", quality });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([blob], newName || "photo.jpg", { type: "image/jpeg" });
  } catch (e) {
    // Safari on iOS 17+ decodes HEIC natively via canvas, so the caller's
    // compression pipeline may still work on the raw HEIC. Return the original
    // so we don't lose the photo — the compressor will fall through to a
    // pass-through if it can't decode.
    if (typeof console !== "undefined") console.warn("HEIC conversion failed", e);
    return file;
  }
}
